/**
 * Artifact Powers — runtime helpers for the special paddles + balls.
 *
 * Wiring contract:
 *   1. Frontend fetches `/api/artifacts` on game mount and stores
 *      `{ equipped_paddle, equipped_ball, levels[id], catalog[] }`
 *      in `artifactsRef.current`.
 *   2. Powers are skipped entirely in competitive modes (`tournament`,
 *      `wager`) — gating happens at the call site.
 *   3. Each power has a per-life state that resets on life loss
 *      (call `resetPerLifeState(artifactsRef)` in the life-decrement
 *      branch).
 *   4. Currently implemented: magnet (paddle), aegis (paddle), bomb
 *      (ball), chain (ball). Laser + Phase are scaffolded for v2.
 */

/** Default state stored under `artifactsRef.current`. */
export function defaultArtifactState() {
  return {
    paddle: null,        // catalog entry or null
    ball: null,
    paddleLevel: 0,
    ballLevel: 0,
    competitive: false,  // true in tournament/wager modes — powers off
    // Per-life state
    aegisChargesLeft: 0,
    phaseChargesLeft: 0,
    bombHits: 0,
    lastBrickKillAt: 0,
    lastBrickKillX: 0,
    lastBrickKillY: 0,
    // Cooldown timer for laser
    laserNextFireAt: 0,
  };
}

/** Hydrate from a fetched `/api/artifacts` payload. */
export function hydrateFromAPI(payload, mode) {
  const state = defaultArtifactState();
  state.competitive = mode === "tournament" || mode === "wager";
  if (!payload) return state;
  const cat = payload.catalog || [];
  const user = payload.user || {};
  if (user.equipped_paddle) {
    const p = cat.find((a) => a.id === user.equipped_paddle);
    if (p) {
      state.paddle = p;
      state.paddleLevel = (user.levels || {})[p.id] || 1;
    }
  }
  if (user.equipped_ball) {
    const b = cat.find((a) => a.id === user.equipped_ball);
    if (b) {
      state.ball = b;
      state.ballLevel = (user.levels || {})[b.id] || 1;
    }
  }
  // Per-life state setup runs even in competitive mode so the helper
  // refs are always initialised — the gameplay helpers individually
  // check `state.competitive` and bail out before applying effects.
  resetPerLifeState(state);
  return state;
}

/**
 * Reset the per-life charges. Called on every life loss + on game start.
 * Magnet + Bomb + Chain don't have per-life state (they're continuous
 * effects), so this only matters for Aegis + Phase.
 */
export function resetPerLifeState(state) {
  if (state.paddle?.power_kind === "aegis") {
    state.aegisChargesLeft = state.paddle.tiers[Math.max(0, state.paddleLevel - 1)].value;
  } else {
    state.aegisChargesLeft = 0;
  }
  if (state.ball?.power_kind === "phase") {
    state.phaseChargesLeft = state.ball.tiers[Math.max(0, state.ballLevel - 1)].value;
  } else {
    state.phaseChargesLeft = 0;
  }
  state.bombHits = 0;
  state.lastBrickKillAt = 0;
}

/** Lookup helper — value for the paddle artifact's current tier. */
export function paddleValue(state) {
  if (!state.paddle) return 0;
  return state.paddle.tiers[Math.max(0, state.paddleLevel - 1)].value;
}

export function ballValue(state) {
  if (!state.ball) return 0;
  return state.ball.tiers[Math.max(0, state.ballLevel - 1)].value;
}

/** Magnet — pull a falling item (moonstone or powerup) toward the
 * paddle when within radius. Mutates `item.x` in place. */
export function applyMagnetPull(state, item, paddleCx, dt) {
  if (state.competitive) return;
  if (state.paddle?.power_kind !== "magnet") return;
  const radius = paddleValue(state);
  if (!radius) return;
  const dx = paddleCx - item.x;
  if (Math.abs(dx) > radius) return;
  const dir = dx === 0 ? 0 : dx / Math.abs(dx);
  const strength = Math.min(Math.abs(dx) * 0.06, 1.2);
  item.x += dir * strength * (dt / 16);
}

/** Aegis — consume one charge to bounce a ball back from the paddle.
 * Returns true if a save fired. */
export function tryAegisSave(state) {
  if (state.competitive) return false;
  if (state.paddle?.power_kind !== "aegis") return false;
  if (state.aegisChargesLeft <= 0) return false;
  state.aegisChargesLeft -= 1;
  return true;
}

/**
 * Bomb — increment hit counter on every brick destroy. When counter
 * reaches the threshold, return an array of nearby brick indices to
 * also destroy (excluding the source brick). Caller passes the full
 * brick list and the source position.
 *
 * Synergy — when the Bomb fires, also light up a Chain-Lightning
 * aftermath: from each radius-hit brick, find one nearest neighbor
 * OUTSIDE the bomb radius and arc to it. Returns `chainIndices` (up
 * to 4, deduped) alongside the regular `indices` so the call site
 * can render the lightning bolts + destroy them. This synergy fires
 * regardless of whether the Chain artifact is equipped — the Bomb
 * ball is already a slot-locked choice; rewarding it with a chained
 * AoE makes the explosion feel like a proper plasma detonation.
 */
export function maybeTriggerBomb(state, blocks, srcX, srcY, srcIdx) {
  if (state.competitive) return null;
  if (state.ball?.power_kind !== "bomb") return null;
  state.bombHits += 1;
  const threshold = ballValue(state);
  if (state.bombHits < threshold) return null;
  state.bombHits = 0;
  const radius = 70;
  const r2 = radius * radius;
  const hits = [];
  for (let i = 0; i < blocks.length; i++) {
    if (i === srcIdx) continue;
    const b = blocks[i];
    const cx = b.x + b.w / 2;
    const cy = b.y + b.h / 2;
    const dx = cx - srcX;
    const dy = cy - srcY;
    if (dx * dx + dy * dy <= r2) hits.push(i);
  }
  // Synergy chain-lightning aftermath — from each radius-hit brick,
  // arc to one nearest neighbor OUTSIDE the radius and outside the
  // existing hit set. Limited to 4 unique chain targets so a tightly
  // packed grid doesn't cascade into an instant-clear (still scales
  // well, just bounded). `arcs` carries source→target coords for
  // the visual lightning streaks.
  const chainRadius = 110;
  const chainR2 = chainRadius * chainRadius;
  const hitSet = new Set(hits);
  const chainIndices = [];
  const arcs = [];
  const MAX_CHAINS = 4;
  for (const srcHitIdx of hits) {
    if (chainIndices.length >= MAX_CHAINS) break;
    const srcBlock = blocks[srcHitIdx];
    if (!srcBlock) continue;
    const sx = srcBlock.x + srcBlock.w / 2;
    const sy = srcBlock.y + srcBlock.h / 2;
    let best = -1;
    let bestD2 = Infinity;
    for (let i = 0; i < blocks.length; i++) {
      if (i === srcIdx || hitSet.has(i) || chainIndices.includes(i)) continue;
      const b = blocks[i];
      const cx = b.x + b.w / 2;
      const cy = b.y + b.h / 2;
      const dx = cx - sx;
      const dy = cy - sy;
      // Must be outside the bomb radius (from the original blast
      // center) so the chain reads as a NEW reach, not a redundant
      // re-hit of bricks the bomb would already kill.
      const dbx = cx - srcX;
      const dby = cy - srcY;
      if (dbx * dbx + dby * dby <= r2) continue;
      const d2 = dx * dx + dy * dy;
      if (d2 <= chainR2 && d2 < bestD2) {
        best = i;
        bestD2 = d2;
      }
    }
    if (best >= 0) {
      chainIndices.push(best);
      const tb = blocks[best];
      arcs.push({
        x1: sx, y1: sy,
        x2: tb.x + tb.w / 2, y2: tb.y + tb.h / 2,
      });
    }
  }
  return { indices: hits, chainIndices, arcs, radius, x: srcX, y: srcY };
}

/**
 * Chain — when a brick is destroyed within 0.4s of the previous
 * destroy AND within `radius` px, return the nearest neighbor's index.
 * Updates the "last kill" tracker on each call.
 */
export function maybeTriggerChain(state, blocks, srcX, srcY, srcIdx, now) {
  if (state.competitive) return null;
  if (state.ball?.power_kind !== "chain") {
    state.lastBrickKillAt = now;
    state.lastBrickKillX = srcX;
    state.lastBrickKillY = srcY;
    return null;
  }
  const elapsed = now - (state.lastBrickKillAt || 0);
  state.lastBrickKillAt = now;
  state.lastBrickKillX = srcX;
  state.lastBrickKillY = srcY;
  if (elapsed > 400) return null;  // 0.4s window
  const radius = ballValue(state);
  const r2 = radius * radius;
  let best = -1;
  let bestD2 = Infinity;
  for (let i = 0; i < blocks.length; i++) {
    if (i === srcIdx) continue;
    const b = blocks[i];
    const cx = b.x + b.w / 2;
    const cy = b.y + b.h / 2;
    const dx = cx - srcX;
    const dy = cy - srcY;
    const d2 = dx * dx + dy * dy;
    if (d2 <= r2 && d2 < bestD2) {
      best = i;
      bestD2 = d2;
    }
  }
  return best >= 0 ? { index: best, radius } : null;
}

/** Whether the ball should phase through bricks this frame
 * (cosumes one charge on the next collision). */
export function tryPhaseSkip(state) {
  if (state.competitive) return false;
  if (state.ball?.power_kind !== "phase") return false;
  if (state.phaseChargesLeft <= 0) return false;
  state.phaseChargesLeft -= 1;
  return true;
}

/** Returns a render-friendly summary for HUD overlays. */
export function summary(state) {
  if (!state || (!state.paddle && !state.ball)) return null;
  const out = {};
  if (state.paddle) {
    out.paddle = {
      name: state.paddle.name,
      icon: state.paddle.icon,
      kind: state.paddle.power_kind,
      level: state.paddleLevel,
      label: state.paddle.tiers[Math.max(0, state.paddleLevel - 1)].label,
      aegisCharges: state.aegisChargesLeft,
    };
  }
  if (state.ball) {
    out.ball = {
      name: state.ball.name,
      icon: state.ball.icon,
      kind: state.ball.power_kind,
      level: state.ballLevel,
      label: state.ball.tiers[Math.max(0, state.ballLevel - 1)].label,
      phaseCharges: state.phaseChargesLeft,
      bombHits: state.bombHits,
    };
  }
  // Laser cooldown progress (0..1, 0 = ready to fire, 1 = full cooldown).
  // Only meaningful when the equipped paddle is a Laser; left undefined
  // otherwise so the HUD doesn't render a noisy ring on Magnet/Aegis.
  if (state.paddle?.power_kind === "laser") {
    const totalMs = paddleValue(state) * 1000;
    const remaining = Math.max(
      0,
      (state.laserNextFireAt || 0) - performance.now()
    );
    out.paddle.laserCooldownPct = totalMs > 0
      ? Math.min(1, remaining / totalMs)
      : 0;
    out.paddle.laserReadyIn = Math.ceil(remaining / 100) / 10;  // seconds, 1 decimal
  }
  return out;
}
